import { lazy, Suspense, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, authFetch, authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { RequireAuth } from "@/components/RequireAuth";
import { Footer } from "@/components/Footer";
import { useAuth, ROLE_LEVEL, type AccessRole } from "@/lib/hooks/useAuth";
import { setImpersonation, type ImpersonationTarget } from "@/lib/impersonation";
import { AdminTemplatesTab } from "./admin/AdminTemplatesTab";
// Lazy: it bundles the widget's sjabloon grammar and the @carmen/i18n catalogues.
const AdminTeamTemplatesTab = lazy(() =>
  import("./admin/AdminTeamTemplatesTab").then((m) => ({ default: m.AdminTeamTemplatesTab })),
);
import { AdminOrganizationsTab } from "./admin/AdminOrganizationsTab";
import { AdminDepartmentsTab } from "./admin/AdminDepartmentsTab";
import { AdminAssociationsTab } from "./admin/AdminAssociationsTab";
import { AdminAudioPreviewTab } from "./admin/AdminAudioPreviewTab";
import { AdminLogsTab } from "./admin/AdminLogsTab";
import { AdminRecordingsTab } from "./admin/AdminRecordingsTab";
import { AdminDeptRecordingsTab } from "./admin/AdminDeptRecordingsTab";
import { AdminRecordingFeedbackTab } from "./admin/AdminRecordingFeedbackTab";
import { AdminTodosTab } from "./admin/AdminTodosTab";
import { AdminRateLimitsTab } from "./admin/AdminRateLimitsTab";
import { AdminSignupBansTab } from "./admin/AdminSignupBansTab";
import { AdminLexiconTab } from "./admin/AdminLexiconTab";
import { AdminTelemetryTab } from "./admin/AdminTelemetryTab";
import { AdminITOverviewTab } from "./admin/AdminITOverviewTab";
import { AdminTierTab } from "./admin/AdminTierTab";
import { AdminRolesPermissionsTab } from "./admin/AdminRolesPermissionsTab";
import { AdminClusterTab } from "./admin/AdminClusterTab";
import { AdminEmailsTab } from "./admin/AdminEmailsTab";
import { AdminOverviewTab } from "./admin/AdminOverviewTab";
import { AdminVisitorsTab } from "./admin/AdminVisitorsTab";
import { AdminUserDrawer } from "./admin/AdminUserDrawer";
import { AdminSidebar } from "./admin/AdminSidebar";
import { AdminPageHeader } from "./admin/AdminPageHeader";
import { hasMinRole, isTab, isTabVisible, type Tab, TAB_MIN_ROLE, TAB_TITLE } from "./admin/adminTabs";
import { subscriptionBadge } from "./admin/subscriptionStatusBadge";
import { Pagination } from "@/components/Pagination";
import { csvFilename, downloadCsv, toCsv } from "@/lib/csv";
import { useHotkeys } from "@/lib/useHotkeys";

type AdminUser = {
  id: string;
  email: string;
  full_name: string;
  created_at: string;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  subscription_status: string;
  approved: boolean;
  approved_at: string | null;
  trial_ends_at: string | null;
  access_role: string;
  organization_id: string | null;
  department_id: string | null;
  association_id: string | null;
  richtlijn_analyse_enabled: boolean;
  allowed_modes_override: string[] | null;
  plan_tier: string;
  recordings_total: number;
  recordings_failed: number;
  recordings_by_source: Record<string, number>;
  usage_total: number;
  usage_errors: number;
  cost_total_usd: number;
  cost_this_month_usd: number;
};

type AdminFeedback = {
  id: string;
  user_id: string;
  user_email?: string;
  category: string;
  message: string;
  created_at: string;
  audio_ref?: string | null;
};

type AdminStats = {
  total_users: number;
  total_recordings: number;
  failed_recordings: number;
  total_dictations: number;
  total_errors: number;
  usage_by_mode: Record<string, number>;
  total_time_saved_seconds: number;
  feedback_count: number;
};

const TAB_STORAGE_KEY = "admin.activeTab";

function loadActiveTab(): Tab {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    if (raw && isTab(raw)) return raw;
  } catch {
    // localStorage may be disabled — fall through to default
  }
  return "overview";
}

function saveActiveTab(tab: Tab): void {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // localStorage may be disabled — silently ignore
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const ROLE_OPTIONS = ["user", "it_admin", "afdeling_secretary", "association_admin", "department_admin", "hospital_admin", "superuser"];
const ROLE_OPTION_LABELS: Record<string, string> = {
  user: "Gebruiker",
  it_admin: "IT-beheerder",
  afdeling_secretary: "Afdelingssecretaris",
  association_admin: "Associatiebeheerder",
  department_admin: "Afdelingsbeheerder",
  hospital_admin: "Organisatiebeheerder",
  superuser: "Superuser",
};
const PLAN_OPTIONS = ["light", "basic", "full", "trial"];

type OrgOption = { id: string; name: string };
type DeptOption = { id: string; name: string; organization_id: string };
type AssocOption = { id: string; name: string; department_id: string };

function UserEditModal({
  user,
  onClose,
  onSaved,
  assignerRole,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
  assignerRole: AccessRole;
}) {
  const [approved, setApproved] = useState(user.approved);
  const [accessRole, setAccessRole] = useState(user.access_role);
  const [planTier, setPlanTier] = useState(user.plan_tier);
  const [trialEndsAt, setTrialEndsAt] = useState(
    user.trial_ends_at ? user.trial_ends_at.slice(0, 10) : "",
  );
  const [richtlijnEnabled, setRichtlijnEnabled] = useState(user.richtlijn_analyse_enabled);
  const [fullName, setFullName] = useState(user.full_name ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [organizationId, setOrganizationId] = useState(user.organization_id ?? "");
  const [departmentId, setDepartmentId] = useState(user.department_id ?? "");
  const [associationId, setAssociationId] = useState(user.association_id ?? "");
  const [allOrgs, setAllOrgs] = useState<OrgOption[]>([]);
  const [allDepts, setAllDepts] = useState<DeptOption[]>([]);
  const [allAssocs, setAllAssocs] = useState<AssocOption[]>([]);

  useEffect(() => {
    void Promise.all([
      authFetchJson<{ organizations: OrgOption[] }>("/api/admin/organizations").catch(() => ({ organizations: [] })),
      authFetchJson<{ departments: DeptOption[] }>("/api/admin/departments").catch(() => ({ departments: [] })),
      authFetchJson<{ associations: AssocOption[] }>("/api/admin/associations").catch(() => ({ associations: [] })),
    ]).then(([orgs, depts, assocs]) => {
      setAllOrgs(orgs.organizations);
      setAllDepts(depts.departments);
      setAllAssocs(assocs.associations);
    });
  }, []);

  const filteredDepts = organizationId
    ? allDepts.filter((d) => d.organization_id === organizationId)
    : allDepts;
  const filteredAssocs = departmentId
    ? allAssocs.filter((a) => a.department_id === departmentId)
    : allAssocs;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const trimmedName = fullName.trim();
      const trimmedEmail = email.trim().toLowerCase();
      const profileUpdate: { full_name?: string; email?: string } = {};
      if (trimmedName !== (user.full_name ?? "").trim()) profileUpdate.full_name = trimmedName;
      if (trimmedEmail !== (user.email ?? "").trim().toLowerCase()) profileUpdate.email = trimmedEmail;
      if (Object.keys(profileUpdate).length > 0) {
        await authFetchJson(`/api/admin/users/${user.id}/profile`, {
          method: "PATCH",
          body: JSON.stringify(profileUpdate),
        });
      }
      if (approved !== user.approved) {
        const action = approved ? "approve" : "reject";
        await authFetch(`/api/admin/users/${user.id}/${action}`, { method: "POST" });
      }
      const trialDateChanged =
        trialEndsAt !== (user.trial_ends_at ?? "").slice(0, 10);
      const planTierChanged = planTier !== user.plan_tier;

      if (planTierChanged || (planTier === "trial" && trialDateChanged)) {
        const body: {
          plan_tier: string;
          trial_ends_at?: string;
        } = { plan_tier: planTier };
        if (planTier === "trial" && trialEndsAt) {
          body.trial_ends_at = trialEndsAt + "T23:59:59Z";
        }
        await authFetchJson(`/api/admin/users/${user.id}/features`, {
          method: "PATCH",
          body: JSON.stringify({
            richtlijn_analyse_enabled: richtlijnEnabled,
            ...body,
          }),
        });
      } else if (richtlijnEnabled !== user.richtlijn_analyse_enabled) {
        // Plan unchanged, just toggle the feature flag.
        await authFetchJson(`/api/admin/users/${user.id}/features`, {
          method: "PATCH",
          body: JSON.stringify({
            richtlijn_analyse_enabled: richtlijnEnabled,
          }),
        });
      }
      const roleChanged = accessRole !== user.access_role;
      const scopeChanged =
        organizationId !== (user.organization_id ?? "") ||
        departmentId !== (user.department_id ?? "") ||
        associationId !== (user.association_id ?? "");
      if (roleChanged || scopeChanged) {
        await authFetchJson(`/api/users/${user.id}/role`, {
          method: "PUT",
          body: JSON.stringify({
            access_role: accessRole,
            organization_id: organizationId || null,
            department_id: departmentId || null,
            association_id: associationId || null,
          }),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const canAssignRoles = ROLE_LEVEL[assignerRole] >= ROLE_LEVEL["department_admin"];
  const assignableRoles = ROLE_OPTIONS.filter(
    (r) => r !== "superuser" && ROLE_LEVEL[r as AccessRole] <= ROLE_LEVEL[assignerRole],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-[0_24px_60px_rgba(10,52,96,0.18)] max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--cs-navy)]">Gebruiker bewerken</h2>
          <button type="button" onClick={onClose} className="text-[var(--cs-ink-subtle)] hover:text-[var(--cs-navy)]">
            ✕
          </button>
        </div>

        <div className="mb-3 rounded-lg bg-[var(--cs-ivory)] p-3 text-xs text-[rgba(10,52,96,0.78)]">
          <p>ID: {user.id.slice(0, 8)}…</p>
        </div>

        {error ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Naam</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">E-mailadres</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={approved}
              onChange={(e) => setApproved(e.target.checked)}
              className="rounded border-[rgba(10,52,96,0.18)]"
            />
            Goedgekeurd
          </label>

          {canAssignRoles ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Toegangsrol</label>
              <select
                value={accessRole}
                onChange={(e) => setAccessRole(e.target.value)}
                className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
              >
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_OPTION_LABELS[r] ?? r.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Organisatie</label>
            <select
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setDepartmentId("");
                setAssociationId("");
              }}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            >
              <option value="">— Geen —</option>
              {allOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Afdeling</label>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setAssociationId("");
              }}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            >
              <option value="">— Geen —</option>
              {filteredDepts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Associatie</label>
            <select
              value={associationId}
              onChange={(e) => setAssociationId(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            >
              <option value="">— Geen —</option>
              {filteredAssocs.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Plan</label>
            <select
              value={planTier}
              onChange={(e) => setPlanTier(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            >
              {PLAN_OPTIONS
                .filter((p) => !(p === "trial" && accessRole === "superuser"))
                .map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
            </select>
          </div>

          {planTier === "trial" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                Trial eindigt op
              </label>
              <input
                type="date"
                value={trialEndsAt}
                onChange={(e) => setTrialEndsAt(e.target.value)}
                className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={richtlijnEnabled}
              onChange={(e) => setRichtlijnEnabled(e.target.checked)}
              className="rounded border-[rgba(10,52,96,0.18)]"
            />
            Richtlijn-analyse
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="rounded-full bg-[var(--cs-rose)] px-5 py-2 text-sm font-medium text-white shadow-[0_12px_28px_rgba(225,29,72,0.20)] hover:bg-[var(--cs-rose-dark)] hover:-translate-y-[1px] transition-all disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

type SortKey = "gebruiker" | "gebruik" | "organisatie" | "afdeling" | "associatie" | "aangemaakt";
type SortDir = "asc" | "desc";

function AdminPageInner() {
  const { accessRole, user, impersonating } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>(loadActiveTab);

  useEffect(() => {
    saveActiveTab(activeTab);
  }, [activeTab]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [feedback, setFeedback] = useState<AdminFeedback[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState("");
  const [busyUserId, setBusyUserId] = useState("");
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [drawerUser, setDrawerUser] = useState<{
    id: string;
    email: string;
    full_name: string | null;
  } | null>(null);
  const [orgMap, setOrgMap] = useState<Record<string, string>>({});
  const [deptMap, setDeptMap] = useState<Record<string, string>>({});
  const [assocMap, setAssocMap] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [columnFilters, setColumnFilters] = useState<Record<SortKey, string>>({
    gebruiker: "",
    gebruik: "",
    organisatie: "",
    afdeling: "",
    associatie: "",
    aangemaakt: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const setColumnFilter = (key: SortKey, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const isSuperuser = hasMinRole(accessRole, "superuser");

  const handleLoadError = useCallback((err: unknown) => {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      setError("Geen admin-rechten.");
    } else {
      setError(err instanceof Error ? err.message : "Fout bij laden.");
    }
  }, []);

  const loadScopes = useCallback(async () => {
    try {
      const [orgs, depts, assocs] = await Promise.all([
        authFetchJson<{ organizations: OrgOption[] }>("/api/admin/organizations").catch(() => ({ organizations: [] })),
        authFetchJson<{ departments: DeptOption[] }>("/api/admin/departments").catch(() => ({ departments: [] })),
        authFetchJson<{ associations: AssocOption[] }>("/api/admin/associations").catch(() => ({ associations: [] })),
      ]);
      setOrgMap(Object.fromEntries((orgs.organizations ?? []).map((o) => [o.id, o.name])));
      setDeptMap(Object.fromEntries((depts.departments ?? []).map((d) => [d.id, d.name])));
      setAssocMap(Object.fromEntries((assocs.associations ?? []).map((a) => [a.id, a.name])));
    } catch (err) {
      handleLoadError(err);
    }
  }, [handleLoadError]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetchJson<{ users: AdminUser[] }>("/api/admin/users");
      setUsers(res.users ?? []);
    } catch (err) {
      handleLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [handleLoadError]);

  const loadFeedback = useCallback(async () => {
    if (!isSuperuser) return;
    setLoading(true);
    setError("");
    try {
      const res = await authFetchJson<{ feedback: AdminFeedback[] }>("/api/admin/feedback");
      setFeedback(res.feedback ?? []);
    } catch (err) {
      handleLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [handleLoadError, isSuperuser]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetchJson<AdminStats>("/api/admin/stats").catch(() => null);
      setStats(res);
    } catch (err) {
      handleLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [handleLoadError]);

  const loadAll = useCallback(async () => {
    await Promise.all([loadScopes(), loadUsers()]);
  }, [loadScopes, loadUsers]);

  useEffect(() => {
    void loadScopes();
  }, [loadScopes]);

  useEffect(() => {
    if (activeTab === "users") void loadUsers();
    else if (activeTab === "feedback") void loadFeedback();
    else if (activeTab === "stats") void loadStats();
    else setLoading(false);
  }, [activeTab, loadUsers, loadFeedback, loadStats]);

  const runUserAction = useCallback(
    async (userId: string, action: string) => {
      setBusyUserId(userId);
      try {
        await authFetch(`/api/admin/users/${userId}/${action}`, { method: "POST" });
        await loadAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Actie mislukt.");
      } finally {
        setBusyUserId("");
      }
    },
    [loadAll],
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      if (!window.confirm("Gebruiker permanent verwijderen? Dit kan niet ongedaan worden."))
        return;
      setBusyUserId(userId);
      try {
        await authFetch(`/api/admin/users/${userId}`, { method: "DELETE" });
        await loadAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
      } finally {
        setBusyUserId("");
      }
    },
    [loadAll],
  );

  const startImpersonating = useCallback(
    async (u: AdminUser) => {
      if (u.id === user?.id) {
        window.alert("U kunt uzelf niet impersoneren.");
        return;
      }
      setBusyUserId(u.id);
      try {
        const target = await authFetchJson<ImpersonationTarget>(
          `/api/admin/impersonate/${u.id}`,
          { method: "POST" },
        );
        // AdminPage remounts on the impersonation change and reloads as the
        // target, so the superuser sees /admin with the target's role.
        setImpersonation(target);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Impersonatie starten mislukt.");
      } finally {
        setBusyUserId("");
      }
    },
    [user?.id],
  );

  const sendPasskeyReset = useCallback(async (u: AdminUser) => {
    if (
      !window.confirm(
        `Passkey reset voor ${u.email}? ` +
          `De gebruiker ontvangt een e-mail om een nieuwe passkey in te stellen. ` +
          `Bestaande passkeys blijven werken tot de nieuwe passkey is ingesteld en ` +
          `worden dan verwijderd. Doorgaan?`,
      )
    )
      return;
    setBusyUserId(u.id);
    try {
      await authFetch(`/api/admin/passkey/user/${u.id}/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_email: u.email }),
      });
      window.alert(`Passkey reset uitgevoerd; magic link verstuurd naar ${u.email}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey reset mislukt.");
    } finally {
      setBusyUserId("");
    }
  }, []);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      // Dates: newest-first is the useful default.
      setSortDir(key === "aangemaakt" ? "desc" : "asc");
    }
  };

  const resolveColumn = (u: AdminUser, key: SortKey): string => {
    switch (key) {
      case "gebruiker":
        return ((u.email ?? "") + " " + (u.full_name ?? "")).toLowerCase();
      case "gebruik":
        return String(u.recordings_total);
      case "organisatie":
        return (u.organization_id ? orgMap[u.organization_id] ?? "" : "").toLowerCase();
      case "afdeling":
        return (u.department_id ? deptMap[u.department_id] ?? "" : "").toLowerCase();
      case "associatie":
        return (u.association_id ? assocMap[u.association_id] ?? "" : "").toLowerCase();
      case "aangemaakt":
        return fmtDate(u.created_at);
    }
  };

  const deferredFilterText = useDeferredValue(filterText);
  const deferredColumnFilters = useDeferredValue(columnFilters);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (deferredFilterText) {
        const haystack = (u.email + (u.full_name ?? "") + (u.access_role ?? "") +
          (u.organization_id ? (orgMap[u.organization_id] ?? "") : "") +
          (u.department_id ? (deptMap[u.department_id] ?? "") : "") +
          (u.association_id ? (assocMap[u.association_id] ?? "") : ""))
          .toLowerCase();
        if (!haystack.includes(deferredFilterText.toLowerCase())) return false;
      }
      for (const key of Object.keys(deferredColumnFilters) as SortKey[]) {
        const f = deferredColumnFilters[key];
        if (!f) continue;
        const val = resolveColumn(u, key);
        if (!val.includes(f.toLowerCase())) return false;
      }
      return true;
    });
    // resolveColumn closes over orgMap/deptMap/assocMap so they're already in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, deferredFilterText, deferredColumnFilters, orgMap, deptMap, assocMap]);

  useEffect(() => {
    setPage(0);
  }, [filterText, columnFilters, sortKey, sortDir, pageSize]);

  const sortedUsers = useMemo(() => {
    if (!sortKey) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      let aVal = "";
      let bVal = "";
      switch (sortKey) {
        case "gebruiker":
          aVal = (a.email ?? "").toLowerCase();
          bVal = (b.email ?? "").toLowerCase();
          break;
        case "gebruik":
          return sortDir === "asc"
            ? a.recordings_total - b.recordings_total
            : b.recordings_total - a.recordings_total;
        case "aangemaakt": {
          const aTs = Date.parse(a.created_at) || 0;
          const bTs = Date.parse(b.created_at) || 0;
          return sortDir === "asc" ? aTs - bTs : bTs - aTs;
        }
        case "organisatie":
          aVal = (a.organization_id ? orgMap[a.organization_id] ?? "" : "").toLowerCase();
          bVal = (b.organization_id ? orgMap[b.organization_id] ?? "" : "").toLowerCase();
          break;
        case "afdeling":
          aVal = (a.department_id ? deptMap[a.department_id] ?? "" : "").toLowerCase();
          bVal = (b.department_id ? deptMap[b.department_id] ?? "" : "").toLowerCase();
          break;
        case "associatie":
          aVal = (a.association_id ? assocMap[a.association_id] ?? "" : "").toLowerCase();
          bVal = (b.association_id ? assocMap[b.association_id] ?? "" : "").toLowerCase();
          break;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortKey, sortDir, orgMap, deptMap, assocMap]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedUsers = sortedUsers.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  const visibleTabs = useMemo(
    () =>
      new Set<Tab>(
        (Object.keys(TAB_MIN_ROLE) as Tab[]).filter((tab) => isTabVisible(accessRole, tab)),
      ),
    [accessRole],
  );

  useEffect(() => {
    if (visibleTabs.size > 0 && !visibleTabs.has(activeTab)) {
      const first = visibleTabs.values().next().value;
      if (first) setActiveTab(first);
    }
  }, [visibleTabs, activeTab]);

  const goTab = useCallback(
    (tab: Tab) => {
      if (visibleTabs.has(tab)) setActiveTab(tab);
    },
    [visibleTabs],
  );

  const focusFilter = useCallback(() => {
    if (activeTab === "users" && filterInputRef.current) {
      filterInputRef.current.focus();
      filterInputRef.current.select();
    }
  }, [activeTab]);

  const hotkeyMap = useMemo(
    () => ({
      "g+o": () => goTab("overview"),
      "g+u": () => goTab("users"),
      "g+s": () => goTab("stats"),
      "g+f": () => goTab("feedback"),
      "g+t": () => goTab("templates"),
      "g+r": () => goTab("recordings"),
      "g+l": () => goTab("logs"),
      "/": focusFilter,
    }),
    [goTab, focusFilter],
  );

  useHotkeys(hotkeyMap);

  if (visibleTabs.size === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--cs-ivory)] p-6">
        <div className="glass-card max-w-md rounded-2xl border border-[rgba(10,52,96,0.10)] p-6 text-center">
          <p className="text-sm text-red-600">Onvoldoende rechten voor deze pagina.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[var(--cs-ivory)]">
      <AdminSidebar
        active={activeTab}
        onSelect={(tab) => setActiveTab(tab as Tab)}
        visibleTabs={visibleTabs as Set<Parameters<typeof AdminSidebar>[0]["active"]>}
        user={user}
        accessRole={accessRole}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <AdminPageHeader
          title={TAB_TITLE[activeTab]}
          user={user}
          accessRole={accessRole}
          onOpenMobileMenu={() => setMobileMenuOpen(true)}
        />

        <main className="w-full flex-1 space-y-4 px-6 py-6">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading && activeTab !== "overview" ? (
          <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
        ) : null}

        {/* OVERVIEW TAB */}
        {activeTab === "overview" ? (
          <AdminOverviewTab
            isSuperuser={isSuperuser}
            defaultOrgId={
              ((user?.app_metadata as Record<string, unknown> | undefined)?.organization_id as
                | string
                | null
                | undefined) ?? null
            }
          />
        ) : null}

        {/* USERS TAB */}
        {activeTab === "users" && !loading ? (
          <>
            <div className="flex items-center gap-2">
              <input
                ref={filterInputRef}
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filter op email, naam, rol, organisatie, afdeling of associatie  (druk / )"
                className="flex-1 rounded-lg border border-[rgba(10,52,96,0.18)] bg-white px-3 py-2 text-sm text-[var(--cs-navy)] placeholder:text-[var(--cs-ink-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
              />
              <span className="text-xs text-[var(--cs-ink-muted)]">
                {filteredUsers.length} / {users.length}
              </span>
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  const rows = sortedUsers.map((u) => ({
                    id: u.id,
                    email: u.email,
                    full_name: u.full_name,
                    access_role: u.access_role,
                    approved: u.approved,
                    subscription_status: u.subscription_status,
                    plan_tier: u.plan_tier,
                    organisatie: u.organization_id ? orgMap[u.organization_id] ?? "" : "",
                    afdeling: u.department_id ? deptMap[u.department_id] ?? "" : "",
                    associatie: u.association_id ? assocMap[u.association_id] ?? "" : "",
                    created_at: u.created_at,
                    last_sign_in_at: u.last_sign_in_at ?? "",
                    recordings_total: u.recordings_total,
                    dictations_total: u.recordings_by_source.live ?? 0,
                    conversations_total: u.recordings_by_source.ambient ?? 0,
                    recordings_failed: u.recordings_failed,
                    usage_total: u.usage_total,
                    usage_errors: u.usage_errors,
                    cost_total_usd: u.cost_total_usd,
                    cost_this_month_usd: u.cost_this_month_usd,
                    trial_ends_at: u.trial_ends_at ?? "",
                  }));
                  const csv = toCsv(rows, [
                    { key: "id", label: "ID" },
                    { key: "email", label: "E-mail" },
                    { key: "full_name", label: "Naam" },
                    { key: "access_role", label: "Toegangsrol" },
                    { key: "approved", label: "Goedgekeurd" },
                    { key: "subscription_status", label: "Abonnement" },
                    { key: "plan_tier", label: "Plan" },
                    { key: "organisatie", label: "Organisatie" },
                    { key: "afdeling", label: "Afdeling" },
                    { key: "associatie", label: "Associatie" },
                    { key: "created_at", label: "Aangemaakt" },
                    { key: "last_sign_in_at", label: "Laatste login" },
                    { key: "recordings_total", label: "Opnames totaal" },
                    { key: "dictations_total", label: "Dictaten" },
                    { key: "conversations_total", label: "Gesprekken" },
                    { key: "recordings_failed", label: "Mislukte opnames" },
                    { key: "usage_total", label: "Verwerkingsruns (telemetrie)" },
                    { key: "usage_errors", label: "Verwerkingsfouten (telemetrie)" },
                    { key: "cost_total_usd", label: "Kosten totaal (USD)" },
                    { key: "cost_this_month_usd", label: "Kosten deze maand (USD)" },
                    { key: "trial_ends_at", label: "Trial tot" },
                  ]);
                  downloadCsv(csvFilename("gebruikers", today, today), csv);
                }}
                className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-2 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
              >
                Exporteer gefilterde lijst
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
              <table className="w-full text-sm">
                <thead className="bg-[var(--cs-ivory)]">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                    <SortableHeader label="Gebruiker" sortKey="gebruiker" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.gebruiker} onFilter={(v) => setColumnFilter("gebruiker", v)} />
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Rol / Plan</th>
                    <SortableHeader label="Organisatie" sortKey="organisatie" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.organisatie} onFilter={(v) => setColumnFilter("organisatie", v)} />
                    <SortableHeader label="Afdeling" sortKey="afdeling" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.afdeling} onFilter={(v) => setColumnFilter("afdeling", v)} />
                    <SortableHeader label="Associatie" sortKey="associatie" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.associatie} onFilter={(v) => setColumnFilter("associatie", v)} />
                    <SortableHeader label="Aangemaakt" sortKey="aangemaakt" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.aangemaakt} onFilter={(v) => setColumnFilter("aangemaakt", v)} />
                    <SortableHeader label="Opnames" sortKey="gebruik" activeSortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} filterValue={columnFilters.gebruik} onFilter={(v) => setColumnFilter("gebruik", v)} />
                    <th className="px-4 py-2">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                  {pagedUsers.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      busy={busyUserId === u.id}
                      expanded={expandedUserId === u.id}
                      isSuperuser={isSuperuser}
                      canImpersonate={isSuperuser && !impersonating}
                      orgName={u.organization_id ? orgMap[u.organization_id] ?? "—" : "—"}
                      deptName={u.department_id ? deptMap[u.department_id] ?? "—" : "—"}
                      assocName={u.association_id ? assocMap[u.association_id] ?? "—" : "—"}
                      onToggleExpand={() =>
                        setExpandedUserId(expandedUserId === u.id ? null : u.id)
                      }
                      onEdit={() => setEditingUser(u)}
                      onApprove={() => void runUserAction(u.id, "approve")}
                      onVerifyEmail={() => void runUserAction(u.id, "verify-email")}
                      onDelete={() => void deleteUser(u.id)}
                      onPasskeyReset={() => void sendPasskeyReset(u)}
                      onImpersonate={() => void startImpersonating(u)}
                      onAnalytics={() =>
                        setDrawerUser({
                          id: u.id,
                          email: u.email,
                          full_name: u.full_name || null,
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              total={sortedUsers.length}
              page={currentPage}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
            <AdminUserDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
          </>
        ) : null}

        {/* FEEDBACK TAB */}
        {activeTab === "feedback" && !loading ? (
          <div className="space-y-2">
            {feedback.length === 0 ? (
              <p className="text-sm text-[var(--cs-ink-muted)]">Geen feedback.</p>
            ) : null}
            {feedback.map((f) => (
              <article
                key={f.id}
                className="glass-card rounded-2xl border border-[rgba(10,52,96,0.10)] p-4"
              >
                <div className="flex items-center gap-2 text-xs text-[var(--cs-ink-muted)]">
                  <span
                    className={`rounded px-1.5 py-0.5 font-medium ${
                      f.category === "error"
                        ? "bg-red-100 text-red-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {f.category}
                  </span>
                  <span>{f.user_email ?? f.user_id}</span>
                  <span>·</span>
                  <span>{new Date(f.created_at).toLocaleString("nl-BE")}</span>
                  {f.audio_ref ? (
                    <span className="ml-auto text-[10px]">audio: {f.audio_ref}</span>
                  ) : null}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--cs-navy)]">{f.message}</p>
              </article>
            ))}
          </div>
        ) : null}

        {/* STATS TAB */}
        {activeTab === "stats" && !loading ? (
          stats ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <StatCard label="Totaal gebruikers" value={stats.total_users} />
              <StatCard label="Totaal opnames" value={stats.total_recordings} />
              <StatCard label="Mislukte opnames" value={stats.failed_recordings} />
              <StatCard label="Feedback" value={stats.feedback_count} />
              <StatCard
                label="Tijd bespaard (uur)"
                value={Math.round(stats.total_time_saved_seconds / 3600)}
              />
            </div>
          ) : (
            <p className="text-sm text-[var(--cs-ink-muted)]">Statistieken niet beschikbaar.</p>
          )
        ) : null}

        {/* TEMPLATES TAB */}
        {activeTab === "templates" ? <AdminTemplatesTab /> : null}

        {/* TEAM SJABLONEN TAB */}
        {activeTab === "team_templates" ? (
          <Suspense fallback={<p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>}>
            <AdminTeamTemplatesTab />
          </Suspense>
        ) : null}

        {/* ORGANIZATIONS TAB */}
        {activeTab === "organizations" ? <AdminOrganizationsTab /> : null}

        {/* DEPARTMENTS TAB */}
        {activeTab === "departments" ? <AdminDepartmentsTab /> : null}

        {/* ASSOCIATIONS TAB */}
        {activeTab === "associations" ? <AdminAssociationsTab /> : null}

        {/* AUDIO PREVIEW TAB */}
        {activeTab === "audio_preview" ? <AdminAudioPreviewTab /> : null}

        {/* LOGS TAB */}
        {activeTab === "logs" ? <AdminLogsTab /> : null}

        {/* RECORDINGS TAB */}
        {activeTab === "recordings" && isSuperuser ? <AdminRecordingsTab /> : null}

        {/* DEPARTMENT RECORDINGS TAB */}
        {activeTab === "dept_recordings" ? <AdminDeptRecordingsTab /> : null}

        {/* RECORDING FEEDBACK TAB */}
        {activeTab === "recording_feedback" && isSuperuser ? <AdminRecordingFeedbackTab /> : null}

        {/* TODOS TAB */}
        {activeTab === "todos" && visibleTabs.has("todos") ? <AdminTodosTab /> : null}

        {/* ROLES & PERMISSIONS TAB */}
        {activeTab === "roles_permissions" && isSuperuser ? <AdminRolesPermissionsTab /> : null}

        {/* RATE LIMITS TAB */}
        {activeTab === "rate_limits" && isSuperuser ? <AdminRateLimitsTab /> : null}

        {/* SIGNUP BANS TAB */}
        {activeTab === "signup_bans" && isSuperuser ? <AdminSignupBansTab /> : null}

        {/* LEXICON TAB */}
        {activeTab === "lexicon" && isSuperuser ? <AdminLexiconTab /> : null}

        {/* TELEMETRY TAB */}
        {activeTab === "telemetry" && isSuperuser ? <AdminTelemetryTab /> : null}

        {/* IT OVERVIEW TAB */}
        {activeTab === "it_overview" && visibleTabs.has("it_overview") ? (
          <AdminITOverviewTab />
        ) : null}

        {/* TIER TAB */}
        {activeTab === "tier" && isSuperuser ? <AdminTierTab /> : null}

        {/* CLUSTER TAB */}
        {activeTab === "cluster" && isSuperuser ? <AdminClusterTab /> : null}

        {/* EMAILS TAB */}
        {activeTab === "emails" && isSuperuser ? <AdminEmailsTab /> : null}

        {/* VISITORS TAB (Umami iframe) */}
        {activeTab === "visitors" && isSuperuser ? <AdminVisitorsTab /> : null}
        </main>

        <Footer />
      </div>

      {editingUser ? (
        <UserEditModal
          user={editingUser}
          assignerRole={accessRole}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            void loadAll();
          }}
        />
      ) : null}
    </div>
  );
}

function UserRow({
  user: u,
  busy,
  expanded,
  isSuperuser,
  canImpersonate,
  orgName,
  deptName,
  assocName,
  onToggleExpand,
  onEdit,
  onApprove,
  onVerifyEmail,
  onDelete,
  onPasskeyReset,
  onImpersonate,
  onAnalytics,
}: {
  user: AdminUser;
  busy: boolean;
  expanded: boolean;
  isSuperuser: boolean;
  canImpersonate: boolean;
  orgName: string;
  deptName: string;
  assocName: string;
  onToggleExpand: () => void;
  onEdit: () => void;
  onApprove: () => void;
  onVerifyEmail: () => void;
  onDelete: () => void;
  onPasskeyReset: () => void;
  onImpersonate: () => void;
  onAnalytics: () => void;
}) {
  const subscription = subscriptionBadge(u.subscription_status, u.trial_ends_at);
  return (
    <>
      <tr className="text-xs hover:bg-[var(--cs-ivory)]/60">
        <td className="px-4 py-2">
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-left"
          >
            <p className="font-medium text-[var(--cs-navy)]">{u.email}</p>
            <p className="text-[10px] text-[var(--cs-ink-muted)]">{u.full_name || "—"}</p>
          </button>
        </td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap gap-1">
            {u.email_confirmed_at ? (
              <Badge color="green">email ok</Badge>
            ) : (
              <Badge color="amber">email?</Badge>
            )}
            {u.approved ? <Badge color="blue">goedgekeurd</Badge> : <Badge color="red">wachtend</Badge>}
            <Badge color={subscription.color}>{subscription.label}</Badge>
          </div>
        </td>
        <td className="px-4 py-2">
          <p className="text-xs font-medium text-[var(--cs-navy)]">{u.access_role.replace(/_/g, " ")}</p>
          <p className="text-[10px] text-[var(--cs-ink-muted)]">
            {u.plan_tier}
            {u.trial_ends_at ? ` · trial tot ${fmtDate(u.trial_ends_at)}` : ""}
          </p>
        </td>
        <td className="px-4 py-2 text-xs text-[var(--cs-navy)]">{orgName}</td>
        <td className="px-4 py-2 text-xs text-[var(--cs-navy)]">{deptName}</td>
        <td className="px-4 py-2 text-xs text-[var(--cs-navy)]">{assocName}</td>
        <td className="px-4 py-2 text-xs tabular-nums text-[var(--cs-navy)]" title={formatDateTime(u.created_at)}>
          {fmtDate(u.created_at)}
        </td>
        <td className="px-4 py-2 tabular-nums">
          <p className="text-xs text-[var(--cs-navy)]">{u.recordings_total} opnames</p>
          <p className="text-[10px] text-[var(--cs-ink-muted)]">
            {u.recordings_by_source.live ?? 0} dictaten · {u.recordings_by_source.ambient ?? 0} gesprekken
          </p>
          <p className="text-[10px] text-[var(--cs-ink-muted)]">
            ${u.cost_this_month_usd.toFixed(2)} deze maand
          </p>
        </td>
        <td className="px-4 py-2">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[10px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
            >
              Bewerk
            </button>
            <button
              type="button"
              onClick={onAnalytics}
              className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[10px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
            >
              Analytics
            </button>
            <button
              type="button"
              onClick={onPasskeyReset}
              disabled={busy}
              className="rounded border border-amber-200 bg-white px-2 py-0.5 text-[10px] text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              Passkey reset
            </button>
            {canImpersonate ? (
              <button
                type="button"
                onClick={onImpersonate}
                disabled={busy}
                className="rounded border border-violet-200 bg-white px-2 py-0.5 text-[10px] text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                title="Bekijk de app als deze gebruiker (alleen lezen)"
              >
                Impersoneer
              </button>
            ) : null}
            {!u.approved ? (
              <button
                type="button"
                onClick={onApprove}
                disabled={busy}
                className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[10px] text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                Keur goed
              </button>
            ) : null}
            {!u.email_confirmed_at ? (
              <button
                type="button"
                onClick={onVerifyEmail}
                disabled={busy}
                className="rounded border border-green-200 bg-white px-2 py-0.5 text-[10px] text-green-700 hover:bg-green-50 disabled:opacity-50"
              >
                Verifieer
              </button>
            ) : null}
            {isSuperuser ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="rounded border border-red-200 bg-white px-2 py-0.5 text-[10px] text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Verwijder
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={9} className="bg-[var(--cs-ivory)]/80 px-4 py-3">
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs sm:grid-cols-3">
              <Detail label="ID" value={u.id} />
              <Detail label="Volledige naam" value={u.full_name || "—"} />
              <Detail label="Email bevestigd" value={formatDateTime(u.email_confirmed_at)} />
              <Detail label="Laatste login" value={formatDateTime(u.last_sign_in_at)} />
              <Detail label="Aangemaakt" value={formatDateTime(u.created_at)} />
              <Detail label="Goedgekeurd op" value={formatDateTime(u.approved_at)} />
              <Detail label="Abonnement" value={u.subscription_status} />
              <Detail label="Trial tot" value={fmtDate(u.trial_ends_at)} />
              <Detail label="Plan" value={u.plan_tier} />
              <Detail label="Toegangsrol" value={u.access_role} />
              <Detail label="Organisatie" value={u.organization_id?.slice(0, 8) ?? "—"} />
              <Detail label="Afdeling" value={u.department_id?.slice(0, 8) ?? "—"} />
              <Detail label="Associatie" value={u.association_id?.slice(0, 8) ?? "—"} />
              <Detail label="Richtlijn-analyse" value={u.richtlijn_analyse_enabled ? "Ja" : "Nee"} />
              <Detail label="Modes override" value={u.allowed_modes_override?.join(", ") ?? "—"} />
              <Detail label="Totaal opnames" value={String(u.recordings_total)} />
              <Detail label="Dictaten" value={String(u.recordings_by_source.live ?? 0)} />
              <Detail label="Gesprekken" value={String(u.recordings_by_source.ambient ?? 0)} />
              <Detail label="Mislukte opnames" value={String(u.recordings_failed)} />
              <Detail label="Verwerkingsruns" value={String(u.usage_total)} />
              <Detail label="Verwerkingsfouten" value={String(u.usage_errors)} />
              <Detail label="Kosten totaal" value={`$${u.cost_total_usd.toFixed(2)}`} />
              <Detail label="Kosten deze maand" value={`$${u.cost_this_month_usd.toFixed(2)}`} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[var(--cs-ink-muted)]">{label}: </span>
      <span className="font-medium text-[var(--cs-navy)]">{value}</span>
    </div>
  );
}

function Badge({
  color,
  children,
}: {
  color: "green" | "amber" | "red" | "blue" | "emerald" | "violet" | "slate";
  children: React.ReactNode;
}) {
  const colors: Record<string, string> = {
    green: "bg-green-100 text-green-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700",
    slate: "bg-[var(--cs-ivory)] text-[rgba(10,52,96,0.78)]",
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${colors[color]}`}>
      {children}
    </span>
  );
}

function SortableHeader({
  label,
  sortKey: key,
  activeSortKey,
  sortDir,
  onToggle,
  filterValue,
  onFilter,
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  sortDir: SortDir;
  onToggle: (key: SortKey) => void;
  filterValue: string;
  onFilter: (value: string) => void;
}) {
  const active = activeSortKey === key;
  return (
    <th className="px-4 py-2">
      <button
        type="button"
        onClick={() => onToggle(key)}
        className="inline-flex items-center gap-1 hover:text-[var(--cs-navy)]"
      >
        {label}
        <span className={active ? "text-[var(--cs-rose)]" : "text-[var(--cs-ink-subtle)]"}>
          {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
        </span>
      </button>
      <input
        type="text"
        value={filterValue}
        onChange={(e) => onFilter(e.target.value)}
        placeholder="Filter…"
        className="mt-1 block w-full rounded border border-[rgba(10,52,96,0.10)] bg-white px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-[var(--cs-navy)] placeholder:text-[var(--cs-ink-subtle)] focus:outline-none focus:ring-1 focus:ring-[var(--cs-rose)]"
      />
    </th>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="glass-card rounded-2xl border border-[rgba(10,52,96,0.10)] p-5">
      <p className="cs-caption">{label}</p>
      <p className="cs-mono mt-1 text-3xl font-semibold text-[var(--cs-navy)]">{value.toLocaleString("nl-BE")}</p>
    </div>
  );
}

export function AdminPage() {
  const { impersonating } = useAuth();
  // Starting or stopping impersonation swaps the request identity, so every
  // tab must drop its state and refetch: remount on the target id.
  return (
    <RequireAuth>
      <AdminPageInner key={impersonating?.user_id ?? "self"} />
    </RequireAuth>
  );
}
